import * as React from "react";
import { DialogContentProps, type DialogProps } from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";
import { IoCloseSharp, IoSearch } from "react-icons/io5";
import { FaBars } from "react-icons/fa6";

import { cn } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  OverlayScrollbarsComponent,
  OverlayScrollbarsComponentRef,
} from "overlayscrollbars-react";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { InputProps } from "./input";

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
      className,
    )}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

interface CommandDialogProps extends DialogProps {}
interface CommandProps {
  /**
   * Accessible label for this command menu. Not shown visibly.
   */
  label?: string;
  /**
   * Optionally set to `false` to turn off the automatic filtering and sorting.
   * If `false`, you must conditionally render valid items based on the search query yourself.
   */
  shouldFilter?: boolean;
  /**
   * Custom filter function for whether each command menu item should matches the given search query.
   * It should return a number between 0 and 1, with 1 being the best match and 0 being hidden entirely.
   * By default, uses the `command-score` library.
   */
  filter?: (value: string, search: string) => number;
  /**
   * Optional controlled state of the selected command menu item.
   */
  value?: string;
  /**
   * Event handler called when the selected item of the menu changes.
   */
  onValueChange?: (value: string) => void;
  /**
   * Optionally set to `true` to turn on looping around when using the arrow keys.
   */
  loop?: boolean;
}

/*const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
	return (
		<Dialog {...props}>
			<DialogContent className='p-0 overflow-hidden shadow-lg'>
				<Command className='[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5'>
					{children}
				</Command>
			</DialogContent>
		</Dialog>
	);
};*/

const CommandDialog = ({
  children,
  dialogProps,
  dialogContentProps,
  dialogClassName,
  commandProps,
}: {
  children?: React.ReactNode;
  dialogProps?: CommandDialogProps & { showCloseButton?: boolean };
  dialogContentProps?: DialogContentProps;
  dialogClassName?: string;
  commandProps?: CommandProps;
}) => {
  return (
    <Dialog {...dialogProps}>
      <DialogContent
        className={cn(
          "overflow-hidden px-5 py-8 shadow-lg group-data-[dir=ltr]:pr-0 group-data-[dir=rtl]:pl-0",
          dialogClassName,
        )}
        showCloseButton={dialogProps?.showCloseButton}
        {...dialogContentProps}
      >
        <Command
          className="[&_[cmdk-group-heading]]:px-0 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-10 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
          {...commandProps}
        >
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div
    className="flex items-center rounded-full border-2 border-primary px-5 group-data-[dir=ltr]:mr-5 group-data-[dir=rtl]:ml-5"
    cmdk-input-wrapper=""
  >
    <IoSearch className="h-4 w-4 shrink-0 text-primary group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        "flex h-11 w-full rounded-md bg-transparent py-3 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  </div>
));

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandStopInput = React.forwardRef<
  HTMLInputElement,
  InputProps & {
    index: number;
    floor: string;
    listeners?: SyntheticListenerMap;
    containerClassname?: string;
    onRemove?: () => void;
  }
>(
  (
    {
      containerClassname,
      className,
      index,
      floor,
      listeners,
      onRemove,
      ...props
    },
    ref,
  ) => (
    <div
      className={cn(
        "relative flex items-center rounded-full border-2 border-primary px-5 group-data-[dir=ltr]:mr-5 group-data-[dir=rtl]:ml-5",
        containerClassname,
      )}
      cmdk-input-wrapper=""
    >
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-primary text-sm text-primary group-data-[dir=ltr]:mr-3 group-data-[dir=rtl]:ml-3">
        {index}
      </span>
      <input
        type="text"
        ref={ref}
        className={cn(
          "flex h-10 w-full rounded-md bg-transparent py-3 text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
      <div className="text-sm font-semibold group-data-[dir=ltr]:mr-3 group-data-[dir=rtl]:ml-3">
        {floor}
      </div>
      <div
        {...listeners}
        className={cn(
          "cursor-pointer rounded-sm text-foreground/25 ring-offset-background transition-opacity hover:text-primary/70 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-primary group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2",
        )}
      >
        <FaBars className="!h-4 !w-4" />
        <span className="sr-only">Drag&Drop</span>
      </div>
      <div
        className={cn(
          "cursor-pointer rounded-sm text-primary ring-offset-background transition-opacity hover:text-primary/70 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-primary",
        )}
        onClick={onRemove}
      >
        <IoCloseSharp className="!h-6 !w-6" />
        <span className="sr-only">Close</span>
      </div>
    </div>
  ),
);

CommandStopInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List> & {
    scrollRef?: React.ForwardedRef<
      OverlayScrollbarsComponentRef<keyof JSX.IntrinsicElements>
    >;
  }
>(({ className, scrollRef, ...props }, ref) => (
  <OverlayScrollbarsComponent
    className={cn(
      "max-h-[330px] overflow-y-auto overflow-x-hidden group-data-[dir=ltr]:pr-5 group-data-[dir=rtl]:pl-5 lg:max-h-[420px]",
    )}
    options={{
      scrollbars: {},
      overflow: { x: "hidden" },
    }}
    defer
    ref={scrollRef}
  >
    <CommandPrimitive.List ref={ref} className={cn("", className)} {...props} />
  </OverlayScrollbarsComponent>
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="py-6 text-center text-sm"
    {...props}
  />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "[&_[cmdk-group-heading]]:text-md overflow-hidden p-1 pt-0 text-foreground [&_[cmdk-group-heading]]:px-0 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:pt-0 [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground/80",
      className,
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 h-px bg-border", className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1 outline-none aria-selected:text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        "ml-auto text-xs tracking-widest text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
};
CommandShortcut.displayName = "CommandShortcut";

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandStopInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
